import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import { useTheme } from 'next-themes';
import { useLocation } from 'react-router-dom';
import { Renderer, Program, Mesh, Color, Triangle } from 'ogl';
import './Iridescence.css';

const vertexShader = `
attribute vec2 uv;
attribute vec2 position;

varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 0, 1);
}
`;

const fragmentShader = `
precision highp float;

uniform float uTime;
uniform vec3 uColor;
uniform vec3 uResolution;
uniform vec2 uMouse;
uniform float uAmplitude;
uniform float uSpeed;

varying vec2 vUv;

void main() {
  float mr = min(uResolution.x, uResolution.y);
  vec2 uv = (vUv.xy * 2.0 - 1.0) * uResolution.xy / mr;

  uv += (uMouse - vec2(0.5)) * uAmplitude;

  float d = -uTime * 0.5 * uSpeed;
  float a = 0.0;

  for (float i = 0.0; i < 8.0; ++i) {
    a += cos(i - d - a * uv.x);
    d += sin(uv.y * i + a);
  }

  d += uTime * 0.5 * uSpeed;

  vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
  col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * uColor;

  gl_FragColor = vec4(col, 1.0);
}
`;

interface IridescenceProps {
  color?: [number, number, number];
  mouseReact?: boolean;
  amplitude?: number;
  speed?: number;
  className?: string;
  onWebGLReady?: () => void;
}

// Module-level singleton to ensure exactly ONE WebGL context exists per page load
let globalRenderer: Renderer | null = null;
let globalGL: any = null;
let globalContextCreationCount = 0;

const IridescenceComponent = memo(({
  color,
  mouseReact = false,
  amplitude = 0.1,
  speed = 1.0,
  className = '',
  resolutionScale = 1.0,
  onWebGLReady,
  ...rest
}: IridescenceProps & { resolutionScale?: number }) => {
  const { theme } = useTheme();
  const ctnDom = useRef<HTMLDivElement>(null);
  const mousePos = useRef({ x: 0.5, y: 0.5 });
  const programRef = useRef<Program | null>(null);
  const isMountedRef = useRef(true);

  // Use landing page colors based on theme - handle undefined theme during initialization
  const defaultColor = color || ((theme === 'light')
    ? [0.702, 0.612, 0.831] // Purple #b39cd4 (light mode)
    : [0.545, 0.361, 0.965] // Purple #8b5cf6 (dark mode - default)
  );

  useEffect(() => {
    isMountedRef.current = true;
    if (!ctnDom.current) return;

    const ctn = ctnDom.current;
    let animateId: number;
    let isAnimating = false;
    let isVisible = true;

    // Singleton Renderer Initialization
    if (!globalRenderer) {
      if (globalContextCreationCount > 0) {
        console.warn('[Iridescence] Reusing existing context slot, but creating new renderer (should not happen with singleton pattern)');
      }

      try {
        globalRenderer = new Renderer({ alpha: false });
        globalContextCreationCount++;
        globalGL = globalRenderer.gl;
        globalGL.clearColor(0.1, 0.05, 0.2, 1);
        globalGL.canvas.style.opacity = '1';
        globalGL.canvas.style.pointerEvents = 'none';
      } catch (e) {
        console.error('Failed to initialize global WebGL renderer:', e);
        return;
      }
    }

    // Reuse the global canvas
    const canvas = globalGL.canvas;

    // Ensure canvas is in the DOM
    if (ctn.firstChild !== canvas) {
      ctn.appendChild(canvas);
    }

    function resize() {
      if (!globalRenderer || !globalGL || ctn.offsetWidth === 0 || ctn.offsetHeight === 0) return;

      globalRenderer.setSize(ctn.offsetWidth * resolutionScale, ctn.offsetHeight * resolutionScale);

      // Update resolution uniform if program exists
      if (programRef.current) {
        programRef.current.uniforms.uResolution.value = new Color(
          globalGL.canvas.width,
          globalGL.canvas.height,
          globalGL.canvas.width / globalGL.canvas.height
        );
      }
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          if (!programRef.current) {
            setupScene();
          }
          resize();
        }
      }
    });

    resizeObserver.observe(ctn);

    function setupScene() {
      if (!globalRenderer || !globalGL) return;

      try {
        resize();

        // Always recreate program/mesh when reconnecting to verify state, 
        // OR reuse if we wanted to be even more persistent, but recreated is safer for state syncing.
        // Actually, let's reuse if it exists, but updating uniforms is key.

        const geometry = new Triangle(globalGL);

        const program = new Program(globalGL, {
          vertex: vertexShader,
          fragment: fragmentShader,
          uniforms: {
            uTime: { value: 0 },
            uColor: { value: new Color(...defaultColor) },
            uResolution: {
              value: new Color(globalGL.canvas.width, globalGL.canvas.height, globalGL.canvas.width / globalGL.canvas.height)
            },
            uMouse: { value: new Float32Array([mousePos.current.x, mousePos.current.y]) },
            uAmplitude: { value: amplitude },
            uSpeed: { value: speed }
          }
        });
        programRef.current = program;

        const mesh = new Mesh(globalGL, { geometry, program });

        function update(t: number) {
          if (!isMountedRef.current) {
            isAnimating = false;
            return;
          }
          if (!isVisible) {
            isAnimating = false;
            return;
          }

          if (isAnimating && globalRenderer && programRef.current) {
            animateId = requestAnimationFrame(update);
            programRef.current.uniforms.uTime.value = t * 0.001;
            globalRenderer.render({ scene: mesh });
          }
        }

        onWebGLReady?.();

        isAnimating = true;
        animateId = requestAnimationFrame(update);

        // Store update function for visibility handler
        (window as any).__iridescenceUpdate = update;
      } catch (e) {
        console.error("Error setting up WebGL scene:", e);
      }
    }

    function handleVisibilityChange() {
      const wasVisible = isVisible;
      isVisible = !document.hidden;

      if (isVisible && !wasVisible) {
        if (!isAnimating) {
          isAnimating = true;
          animateId = requestAnimationFrame((window as any).__iridescenceUpdate || (() => { }));
        }
      } else if (!isVisible && wasVisible) {
        isAnimating = false;
        cancelAnimationFrame(animateId);
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (ctn.offsetWidth > 0 && ctn.offsetHeight > 0) {
      setupScene();
    }

    return () => {
      isMountedRef.current = false;
      cancelAnimationFrame(animateId);
      resizeObserver.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // DO NOT destroy the renderer. Just remove canvas from DOM.
      // This keeps the context alive but hidden, ready for next mount.
      if (globalGL && globalGL.canvas && ctn.contains(globalGL.canvas)) {
        ctn.removeChild(globalGL.canvas);
      }

      // Cleanup references but keep global stuff
      programRef.current = null;
    };
  }, [defaultColor, speed, amplitude, resolutionScale, onWebGLReady]);

  // Update colors when theme changes
  useEffect(() => {
    if (programRef.current) {
      programRef.current.uniforms.uColor.value = new Color(...defaultColor);
    }
  }, [theme, defaultColor]);

  return (
    <div
      ref={ctnDom}
      className={`iridescence-container ${className}`}
      {...rest}
    />
  );
});

IridescenceComponent.displayName = 'IridescenceComponent';

// Dark mode: Much darker, richer purple iridescence
const DARK_MODE_CONFIG = {
  color: [0.2, 0.05, 0.7] as [number, number, number], // Even darker purple
  amplitude: 0.08,
  speed: 0.23,
  mouseReact: false // Disabled for performance testing
};

// Light mode: Much lighter, subtler iridescence - less colorful
const LIGHT_MODE_CONFIG = {
  color: [0.702, 0.612, 0.831] as [number, number, number], // #b39cd4 purple for light mode
  amplitude: 0.01, // Much more subtle amplitude
  speed: 0.23,
  mouseReact: false // Disabled for performance testing
};

// Iridescence background component for all pages
// Replaces AnimatedGridBackground
const IridescenceBackground = memo(() => {
  const { theme } = useTheme();
  const location = useLocation();
  const [isWebGLReady, setIsWebGLReady] = useState(false);

  const handleWebGLReady = useCallback(() => {
    setIsWebGLReady(true);
  }, []);

  // Determine if we're on landing pages vs app pages
  // Landing pages: full resolution, app pages: 20% resolution for performance
  const isLandingPage = location.pathname.startsWith('/landing') ||
    location.pathname === '/terms' ||
    location.pathname === '/privacy' ||
    location.pathname === '/about' ||
    location.pathname === '/pricing' ||
    location.pathname === '/contact' ||
    location.pathname === '/features' ||
    location.pathname === '/integrations' ||
    location.pathname === '/auth' ||
    location.pathname === '/auth/callback' ||
    location.pathname === '/role-selection' ||
    location.pathname === '/company-selection' ||
    location.pathname === '/data-deletion' ||
    location.pathname === '/';

  const resolutionScale = isLandingPage ? 1.0 : 0.25;

  // Handle undefined theme during initialization - default to dark theme
  // Memoize config to ensure stable reference and prevent WebGL context recreation
  const config = React.useMemo(() => {
    return (theme === 'light') ? LIGHT_MODE_CONFIG : DARK_MODE_CONFIG;
  }, [theme]);

  return (
    <div className="fixed inset-0 -z-1 overflow-hidden">
      <IridescenceComponent
        color={config.color}
        mouseReact={config.mouseReact}
        amplitude={config.amplitude}
        speed={config.speed}
        resolutionScale={resolutionScale}
        className="w-full h-full"
        onWebGLReady={handleWebGLReady}
      />
      {/* Enhanced gradient overlay for depth - only show when WebGL is ready */}
      {isWebGLReady && (
        <div className="absolute inset-0 pointer-events-none z-10">
          {/* Main gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-slate-50/20 dark:to-slate-900/30" />
          {/* Additional depth gradient */}
          <div
            className="absolute inset-0"
            style={{
              background: (theme === 'light')
                ? 'radial-gradient(circle at center, transparent 0%, rgba(179, 156, 212, 0.12) 50%, rgba(179, 156, 212, 0.18) 100%)'
                : 'radial-gradient(circle at center, transparent 0%, rgba(88, 28, 135, 0.3) 50%, rgba(59, 7, 100, 0.4) 100%)'
            }}
          />
          {/* Subtle top gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-purple-800/10 dark:to-purple-900/20" />
        </div>
      )}
    </div>
  );
});

IridescenceBackground.displayName = 'IridescenceBackground';

export default IridescenceBackground;
